import { useCallback } from 'react';

import { useChatReviewModalContext } from '~context/chatReviewModal';
import { FeedbackCategory } from '~entities/feedback';
import type { FeedbackTrigger } from '~features/annals/enums/feedback';
import { FeedbackReason } from '~features/annals/enums/feedback';
import annalsCommon from '~features/annals/hooks/common';
import {
	loadFromSessionStorage,
	saveToSessionStorage,
	ssKeys,
} from '~features/storage';
import { useSession } from '~hooks/useSession';
import { userApi } from '~services/api/user';

export const useChatReview = () => {
	const { userId, isDemoUser } = useSession();
	const { openReviewModal } = useChatReviewModalContext();

	const { data } = userApi.useGetUserFeedbackQuery(
		{
			userId,
		},
		{ skip: isDemoUser || !userId }
	);
	const feedback = data?.[FeedbackCategory.Chats];

	const sendShowFeedbackModal = annalsCommon.useShowFeedbackModal({
		reason: FeedbackReason.ChatExperience,
	});

	const askForReview = useCallback(
		({
			skip,
			force,
			source,
		}: {
			skip?: boolean;
			force?: boolean;
			source?: FeedbackTrigger;
		}) => {
			if (skip) {
				return;
			}

			const hasReviewAsked = loadFromSessionStorage(
				ssKeys.CHAT_EXP_REVIEW_ASKED,
				false
			);

			if ((feedback?.feedbackCount || hasReviewAsked || isDemoUser) && !force) {
				return;
			}

			openReviewModal()();
			saveToSessionStorage(ssKeys.CHAT_EXP_REVIEW_ASKED, true);
			void sendShowFeedbackModal({ modal_view_trigger: source });
		},
		[
			feedback?.feedbackCount,
			isDemoUser,
			openReviewModal,
			sendShowFeedbackModal,
		]
	);

	return {
		askForReview,
	};
};
